import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  pagination as pageConstants,
  state as stateConstants,
} from 'src/constants';
import { FetchData } from 'src/core/api';
import { Loading } from 'src/core/components';
import {
  getList,
  setPagination as setPaginationAction,
} from 'src/core/redux/actions';
import {
  selectListData,
  selectListFetchData,
  selectPagination,
} from 'src/core/redux/selectors';
import { Product } from 'src/models';
import { dateToDisplayFormat } from 'src/utils';

import { deleteProducts as deleteProductsAction } from './actions';
import getTableConfig from './config';
import ProductsTableComponent from './ProductsTableComponent';

const prepareProducts = (products) =>
  products?.map((product) => {
    return {
      checkbox: {
        itemId: product.id,
      },
      nameDescription: {
        name: product.name,
        description: product.description,
        detailsLink: product.detailsLink,
      },
      product: product.type,
      status: product.status,
      lastModified: dateToDisplayFormat(product.lastModified),
      actions: {
        canExport: product.canExport,
        detailsLink: product.detailsLink,
      },
      batchId: product.batchId,
      customProductCategory: product.customProductCategory,
      productCategoryFormat: product.productCategoryFormat,
    };
  });

const ProductsTableContainer = ({
  // products
  products,
  productsObj,
  // totalProducts,
  category,
  subcategory,
  isViewAllActive,
  deleteProducts,
  selectedMenuItem,

  // fetch data props
  totalItems,
  fetchProducts,
  fetchAllProducts,
  fetchDataProps,

  // pagination
  pagination,
  setPagination,
}) => {
  const [selectedProductsIds, setSelectedProductsIds] = React.useState([]);
  const [sortPayload, setSortPayload] = React.useState({
    sortBy: '',
    sortOrder: '',
  });

  // Globally sort table stats
  const [sortProduct, setSortProduct] = React.useState({});
  const [sortProductRes, setSortProductRes] = React.useState([]);
  const [isSortingLoading, setIsSortingLoading] = React.useState(false);

  // Helper function to get current category-subcategory key
  const getCurrentCategoryKey = () => {
    return `${category.name} - ${subcategory.name}`;
  };

  // Function to replace sortProduct data with sortProductRes data
  const replaceSortProductData = (categoryKey) => {
    if (sortProductRes[categoryKey]) {
      setSortProduct((prevSortProduct) => {
        // If it's an object with category keys
        if (
          typeof prevSortProduct === 'object' &&
          !Array.isArray(prevSortProduct)
        ) {
          return {
            ...prevSortProduct,
            [categoryKey]: sortProductRes[categoryKey],
          };
        }
        // If sortProduct is just an array (current category data)
        else {
          return sortProductRes[categoryKey];
        }
      });
    }
  };

  // Function to get current display data
  const getCurrentProductData = () => {
    const currentKey = getCurrentCategoryKey();

    // First check if we have sorted results for current category
    if (sortProductRes[currentKey] && sortProductRes[currentKey].length > 0) {
      return sortProductRes[currentKey];
    }

    // Fall back to original sortProduct data
    if (typeof sortProduct === 'object' && !Array.isArray(sortProduct)) {
      return sortProduct[currentKey] || [];
    }

    // If sortProduct is an array
    return Array.isArray(sortProduct) ? sortProduct : [];
  };

  const fetchProductResponse = async (type, order) => {
    try {
      // Set loading state to true when API call starts
      setIsSortingLoading(true);

      const response = await fetchProducts({
        searchText: selectedMenuItem?.searchText,
        pagination: pageConstants.INITIAL_PAGINATON,
        customProductCategory: category.categoryNr,
        productCategoryFormat: subcategory.subcategoryNr,
        sortBy: type,
        sortOrder: order,
        filter: selectedMenuItem?.filter,
      });
      const modifiedResponse = response?.customDesigns?.map((item) => ({
        id: item.workspaceId,
        name: item.name,
        description: item.description,
        type: item.itemType,
        status: item.status,
        lastModified: item.lastModified,
        detailsLink: item.productLink,
        canExport: item.canExportDesign,
        batchId: item.batchId,
        customProductCategory: item.customProductCategory,
        productCategoryFormat: item.productCategoryFormat,
      }));
      const currentKey = getCurrentCategoryKey();
      setSortProductRes((prev) => {
        const updatedRes = {
          ...prev,
          [currentKey]: modifiedResponse?.slice(0, 5) || [],
        };
        // Automatically replace sortProduct data after API response
        setTimeout(() => {
          replaceSortProductData(currentKey);
        }, 0);
        return updatedRes;
      });

      setSortPayload((prevPayload) => ({
        ...prevPayload,
        sortBy: type,
        sortOrder: order,
      }));
    } catch (error) {
      console.error('Error fetching sorted products:', error);
      // Handle error state if needed
    } finally {
      // Set loading state to false when API call completes (success or error)
      setIsSortingLoading(false);
    }
  };

  const handleSorting = (type, order) => {
    if (!isViewAllActive) {
      setSortPayload((prevPayload) => ({
        ...prevPayload,
        sortBy: type,
        sortOrder: order,
      }));
    } else {
      fetchProductResponse(type, order);
      // handleDataReplacement();
    }
  };

  // Function to manually trigger data replacement
  const handleDataReplacement = () => {
    const currentKey = getCurrentCategoryKey();
    replaceSortProductData(currentKey);
  };

  const onPaginationChange = (latestPagination) => {
    fetchProducts({
      searchText: selectedMenuItem?.searchText,
      pagination: { ...latestPagination },
      customProductCategory: category.categoryNr,
      productCategoryFormat: subcategory.subcategoryNr,
      sortBy: sortPayload.sortBy,
      sortOrder: sortPayload.sortOrder,
      filter: selectedMenuItem?.filter,
    });
  };

  const tableConfigProps = {
    addProductId: (id) => setSelectedProductsIds([...selectedProductsIds, id]),
    removeProductId: (id) =>
      setSelectedProductsIds(
        selectedProductsIds.filter((itemId) => itemId !== id)
      ),
    setSelectedItems: setSelectedProductsIds,
    productsIds: products.map((product) => product.id),
    selectedProductsIds,
    isViewAllActive,
    handleSorting,
    sortPayload,
  };

  const resetData = useCallback(() => setSelectedProductsIds([]), []);

  const onDelete = () => {
    const newPaginationConfig = {
      ...pagination,
      currentPage: pageConstants.INITIAL_PAGE,
    };

    setPagination(newPaginationConfig);

    deleteProducts(
      selectedProductsIds,
      isViewAllActive,
      {
        pagination: newPaginationConfig,
        customProductCategory: category.categoryNr,
        productCategoryFormat: subcategory.subcategoryNr,
      },
      setSelectedProductsIds
    );
  };

  useEffect(() => {
    const currentKey = getCurrentCategoryKey();
    if (sortProductRes[currentKey]) {
      handleDataReplacement();
    }
  }, [sortProductRes]);

  // Effect to handle initial data setup
  useEffect(() => {
    if (!isEmpty(productsObj)) {
      const currentKey = getCurrentCategoryKey();
      setSortProduct((prevState) => {
        // If we already have sorted results, use them instead
        if (sortProductRes[currentKey]) {
          return typeof prevState === 'object' && !Array.isArray(prevState)
            ? { ...prevState, [currentKey]: sortProductRes[currentKey] }
            : sortProductRes[currentKey];
        }
        // Otherwise use original products data
        const originalData = productsObj?.products || [];
        return typeof prevState === 'object' && !Array.isArray(prevState)
          ? { ...prevState, [currentKey]: originalData }
          : originalData;
      });
    }
  }, [productsObj, category.name, subcategory.name]);

  // Effect to replace data when sortProductRes changes
  useEffect(() => {
    const currentKey = getCurrentCategoryKey();
    if (sortProductRes[currentKey]) {
      replaceSortProductData(currentKey);
    }
  }, [sortProductRes, category.name, subcategory.name]);

  useEffect(() => {
    if (!isViewAllActive) {
      setPagination(pageConstants.INITIAL_PAGINATON);

      fetchProducts({
        searchText: selectedMenuItem?.searchText,
        pagination: pageConstants.INITIAL_PAGINATON,
        customProductCategory: category.categoryNr,
        productCategoryFormat: subcategory.subcategoryNr,
        filter: selectedMenuItem?.filter,
        sortBy: selectedMenuItem.sortBy,
        sortOrder: selectedMenuItem.sortOrder,
      });
      setSelectedProductsIds([]);
      setSortPayload({
        sortBy: '',
        sortOrder: '',
      });
    }
  }, [
    fetchProducts,
    category.name,
    subcategory.name,
    selectedMenuItem?.searchText,
    selectedMenuItem?.filter,
    isViewAllActive,
  ]);

  useEffect(() => {
    if (!isViewAllActive) {
      if (!sortPayload.sortBy || !pagination) {
        setPagination(pageConstants.INITIAL_PAGINATON);
      }

      fetchProducts({
        searchText: selectedMenuItem?.searchText,
        pagination: sortPayload.sortBy
          ? !pagination
            ? pageConstants.INITIAL_PAGINATON
            : { ...pagination }
          : pageConstants.INITIAL_PAGINATON,
        customProductCategory: category.categoryNr,
        productCategoryFormat: subcategory.subcategoryNr,
        sortBy: sortPayload.sortBy,
        sortOrder: sortPayload.sortOrder,
        filter: selectedMenuItem?.filter,
      });
    }
  }, [
    sortPayload.sortBy,
    sortPayload.sortOrder,
    category.categoryNr,
    subcategory.subcategoryNr,
    fetchProducts,
    setPagination,
  ]);

  useEffect(() => {
    if (!isViewAllActive) {
      fetchAllProducts({
        customProductCategory: category.categoryNr,
        productCategoryFormat: subcategory.subcategoryNr,
      });
    }
  }, [category.name, subcategory.name]);

  if (isSortingLoading) {
    return <Loading isLoading />;
  }

  return (
    <FetchData
      {...{
        ...fetchDataProps,
        resetData,
      }}
    >
      <ProductsTableComponent
        {...{
          id: `${subcategory ? `${category.name}-${subcategory.name}` : category.name}`,
          data: prepareProducts(
            isViewAllActive ? getCurrentProductData() : products
          ),
          config: getTableConfig(tableConfigProps),
          totalItems,
          onPaginationChange,
          setPagination,
          pagination,
          selectedProductsIds,
          category: category.name,
          subcategory: subcategory.name,
          onDelete,
        }}
      />
    </FetchData>
  );
};

ProductsTableContainer.propTypes = {
  products: PropTypes.array.isRequired,
  productsObj: PropTypes.object,
  totalProducts: PropTypes.array.isRequired,
  setPagination: PropTypes.func.isRequired,
  pagination: PropTypes.object,
  filters: PropTypes.object,
  sortBy: PropTypes.object,
  fetchProducts: PropTypes.func.isRequired,
  fetchAllProducts: PropTypes.func.isRequired,
  setFilters: PropTypes.func,
  setSortBy: PropTypes.func,
  fetchDataProps: PropTypes.object.isRequired,
  totalItems: PropTypes.number.isRequired,
  category: PropTypes.object.isRequired,
  subcategory: PropTypes.object.isRequired,
  isViewAllActive: PropTypes.bool,
  deleteProducts: PropTypes.func.isRequired,
  selectedMenuItem: PropTypes.object,
};

const makeMapStateToProps = () => {
  const selectProducts = selectListData(Product);
  const selectAllProducts = selectListData(
    Product,
    stateConstants.IDENTIFIER_TYPE_TOTAL_LIST
  );

  const mapStateToProps = (state, ownProps) => {
    const { list, totalItems } = selectProducts(state);
    const { list: totalProducts } = selectAllProducts(state);
    const predefinedProducts = ownProps.products;
    const productsObj = { ...ownProps };
    const pagination = ownProps.isViewAllActive
      ? null
      : totalItems >= 10
        ? selectPagination(Product, pageConstants.INITIAL_PAGINATON)(state)
        : null;

    return {
      products: predefinedProducts || list,
      productsObj,
      totalProducts,
      totalItems,
      fetchDataProps: predefinedProducts
        ? {
            isFetching: false,
            fetched: true,
          }
        : selectListFetchData(Product)(state),
      pagination,
    };
  };

  return mapStateToProps;
};

const mapDispatchToProps = {
  fetchProducts: getList(Product),
  fetchAllProducts: getList(Product, stateConstants.IDENTIFIER_TYPE_TOTAL_LIST),
  setPagination: setPaginationAction(Product),
  deleteProducts: deleteProductsAction,
};

export default connect(
  makeMapStateToProps,
  mapDispatchToProps
)(ProductsTableContainer);
